import { components, paths } from "@minuthq/minut-api-types/schema"

import {
  propertyContextQueryCache,
  usePropertyContextQueryCache,
} from "src/data/propertyContext/propertyContextQueryCache"
import {
  TFetchPropertyContextsQuery,
  TPropertyContext,
} from "src/data/propertyContext/propertyContextTypes"
import {
  useMinutDelete,
  useMinutPatch,
  useMinutPost,
  useMinutQuery,
} from "src/hooks/minutQuery"

export function useFetchPropertyContexts({
  orgId,
  filters,
}: {
  orgId: string
  filters?: TFetchPropertyContextsQuery
}) {
  return useMinutQuery<
    "/organizations/{organization_id}/property_context",
    400 | 401 | 403 | 404,
    {
      property_contexts: TPropertyContext[]
      paging: components["schemas"]["PagingInfo"]
    }
  >({
    queryKey: propertyContextQueryCache.propertyContextList(orgId, filters),
    queryPath: `/organizations/${orgId}/property_context`,
    apiPath: "/organizations/{organization_id}/property_context",
    filters,
  })
}

export function useFetchPropertyContext({
  orgId,
  contextId,
  options,
}: {
  orgId: string
  contextId: string
  options?: {
    enabled?: boolean
  }
}) {
  return useMinutQuery<
    "/organizations/{organization_id}/property_context/{property_context_id}",
    400 | 401 | 403 | 404,
    TPropertyContext
  >({
    queryKey: propertyContextQueryCache.propertyContext(orgId, contextId),
    queryPath: `/organizations/${orgId}/property_context/${contextId}`,
    apiPath:
      "/organizations/{organization_id}/property_context/{property_context_id}",
    options,
  })
}

export function usePostPropertyContext() {
  const { invalidatePropertyContextList } = usePropertyContextQueryCache()

  return useMinutPost<
    "/organizations/{organization_id}/property_context",
    400 | 401 | 403 | 404,
    201,
    paths
  >({
    pathFn: (args) => `/organizations/${args.organization_id}/property_context`,
    apiPath: "/organizations/{organization_id}/property_context",
    options: {
      onSuccess: (_, variables) => {
        invalidatePropertyContextList(variables.path.organization_id)
      },
    },
  })
}

export function usePatchPropertyContext() {
  const { invalidatePropertyContextList, invalidatePropertyContext } =
    usePropertyContextQueryCache()

  return useMinutPatch<"/organizations/{organization_id}/property_context/{property_context_id}">(
    {
      apiPath:
        "/organizations/{organization_id}/property_context/{property_context_id}",
      pathFn: (variables) =>
        `/organizations/${variables.organization_id}/property_context/${variables.property_context_id}`,
      options: {
        onSuccess: (_, variables) => {
          invalidatePropertyContextList(variables.path.organization_id)
          invalidatePropertyContext(
            variables.path.organization_id,
            variables.path.property_context_id
          )
        },
      },
    }
  )
}

export function useDeletePropertyContext() {
  const { invalidatePropertyContextList } = usePropertyContextQueryCache()

  return useMinutDelete<"/organizations/{organization_id}/property_context/{property_context_id}">(
    {
      apiPath:
        "/organizations/{organization_id}/property_context/{property_context_id}",
      pathFn: (variables) =>
        `/organizations/${variables.organization_id}/property_context/${variables.property_context_id}`,
      options: {
        onSuccess: (_, variables) => {
          invalidatePropertyContextList(variables.path.organization_id)
        },
      },
    }
  )
}

export function usePostPropertyContextTestMessage() {
  return useMinutPost<
    "/organizations/{organization_id}/property_context/test",
    400 | 401 | 403 | 404,
    // This endpoint returns 200 rather than 201, so we need to specify the return type
    // Ideally minutQuery could take 200 into consideration as well, but I cant make it work :D
    paths["/organizations/{organization_id}/property_context/test"]["post"]["responses"]["200"]["content"]["application/json"]
  >({
    pathFn: (args) =>
      `/organizations/${args.organization_id}/property_context/test`,
    apiPath: "/organizations/{organization_id}/property_context/test",
  })
}
